import { useCallback, useEffect, useState } from 'react';

/**
 * Persisted, app-wide visual theme. Currently two modes:
 *   - default (no attribute on <html>) — the existing app styling
 *   - 'e3d'   — Évangile en 3D conference theme (dark, gold-on-night)
 *
 * The selection is stored in localStorage under `appTheme`. Applying the
 * theme is just `document.documentElement.setAttribute('data-theme', …)`;
 * the actual styling lives in src/styles/e3d-theme.css scoped under
 * [data-theme="e3d"].
 */

const STORAGE_KEY = 'appTheme';
const ALLOWED = new Set(['e3d']);

function readStored() {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v && ALLOWED.has(v) ? v : null;
}

function apply(theme) {
  if (typeof document === 'undefined') return;
  if (theme) {
    document.documentElement.setAttribute('data-theme', theme);
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

/**
 * Apply the stored theme as early as possible (called from src/index.js so
 * there's no flash of unthemed content on reload).
 */
export function bootstrapAppTheme() {
  apply(readStored());
}

export function useAppTheme() {
  const [theme, setThemeState] = useState(() => readStored());

  useEffect(() => {
    apply(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (typeof window !== 'undefined') {
      if (next) window.localStorage.setItem(STORAGE_KEY, next);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'e3d' ? null : 'e3d');
  }, [theme, setTheme]);

  return { theme, isE3D: theme === 'e3d', setTheme, toggleTheme };
}
